"use client";

import React, { useState, useEffect } from "react";
import ProductTile from "@/components/ProductTile";
import type { PerfumeProduct } from "@/services/catalogService";
import Link from "next/link";
import { ChevronRight, ArrowRight, Tag } from "lucide-react";

interface LandingPageClientProps {
  isLoading?: boolean;
  products: PerfumeProduct[];
}

function ProductLoadingGrid() {
  return (
    <div aria-label="Loading fragrances" className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5" role="status">
      {Array.from({ length: 5 }, (_, index) => (
        <div className="animate-pulse border border-border/50 bg-card p-3" key={index}>
          <div className="aspect-[3/4] bg-foreground/10" />
          <div className="mt-4 h-2 w-1/3 bg-foreground/10" />
          <div className="mt-3 h-3 w-4/5 bg-foreground/10" />
          <div className="mt-3 h-3 w-1/4 bg-foreground/10" />
        </div>
      ))}
    </div>
  );
}

export const LandingPageClient: React.FC<LandingPageClientProps> = ({ isLoading = false, products }) => {

  const normalizedCollection = (value: string) => value.trim().toLocaleLowerCase().replace(/s$/, "");
  const inCollection = (product: PerfumeProduct, collection: string) => product.collections.some(
    (value) => normalizedCollection(value) === normalizedCollection(collection),
  );

  // Banner slides setup
  const banners = [
    "landing-page-banner-images/banner-001.webp",
    "landing-page-banner-images/banner-002.webp",
    "landing-page-banner-images/banner-003.webp",
    "landing-page-banner-images/banner-004.webp",
    "landing-page-banner-images/banner-005.webp",
    "landing-page-banner-images/banner-006.webp",
    "landing-page-banner-images/banner-007.webp",
  ];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Collections state
  const collectionTabs = ["Rare", "New", "Partial", "Samples", "Decants"];
  const [activeCollectionTab, setActiveCollectionTab] = useState("Rare");

  // Filters for other rows
  const latestArrivals = products.filter((p) => p.collections.includes("Latest Arrivals") || p.tags.includes("latest"));
  const featuredSelections = products.filter((p) => p.collections.includes("Featured Selections") || p.tags.includes("featured"));
  const travelSprays = products.filter((p) => p.collections.includes("Travel Sprays & Minis") || p.additionalInfo.size === "10ml" || p.additionalInfo.size === "5ml");

  // Active collection products
  const collectionProducts = products.filter((p) => inCollection(p, activeCollectionTab));

  return (
    <div className="space-y-16 pb-20">

      {/* 1. Large Banner Section */}
      <section className="relative h-[80vh] min-h-[500px] w-full bg-neutral-900 overflow-hidden border-b border-border">
        {/* Banner Images */}
        <div className="absolute inset-0 opacity-45">
          <img
            src={`/images/${banners[currentSlide]}`}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            decoding="async"
            fetchPriority={currentSlide === 0 ? "high" : "auto"}
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
        </div>

        {/* Text Overlay */}
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4 z-10 space-y-6">
          <div className="space-y-2 max-w-4xl">
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-extralight uppercase tracking-[0.3em] text-white">
              Axiom Manifold
            </h1>
            <div className="w-24 h-0.5 bg-primary/80 mx-auto my-4" />
            <p className="text-sm sm:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed font-light font-sans tracking-wide italic">
              "There is no scarcity. Only creation.
              <br />
              This is a space where the soul is free to experience the material world."
            </p>
          </div>

          <div className="flex gap-4 pt-4">
            <Link
              href="/catalog"
              className="bg-white text-black hover:bg-primary hover:text-primary-foreground font-semibold px-8 py-3 text-xs uppercase tracking-widest border border-white hover:border-primary transition-all duration-300"
            >
              Explore Catalog
            </Link>
            <Link
              href="/catalog?tag=featured"
              className="bg-transparent text-white hover:bg-white/10 font-semibold px-8 py-3 text-xs uppercase tracking-widest border border-white transition-all duration-300"
            >
              Featured Fragrances
            </Link>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 flex gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-12 h-1 cursor-pointer transition-all duration-300 ${
                  i === currentSlide ? "bg-primary" : "bg-white/30"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 2. Explore Collections Section */}
      <section className="layout-container space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-border/60 pb-4 gap-4">
          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
              Olfactory Families
            </h2>
            <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wider text-foreground">
              Explore Categories
            </h3>
          </div>

          {/* Sub-tabs for collections */}
          <div className="flex flex-wrap gap-2">
            {collectionTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveCollectionTab(tab)}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition-all duration-200 cursor-pointer ${
                  activeCollectionTab === tab
                    ? "bg-foreground text-background border-foreground font-bold"
                    : "bg-background text-foreground/75 border-border hover:border-foreground/45"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Collection Products Row */}
        {isLoading ? <ProductLoadingGrid /> : collectionProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {collectionProducts.slice(0, 5).map((product) => (
              <ProductTile key={product.product_id} product={product} />
            ))}
            {/* Direct Shop shortcut tile */}
            <Link
              href={`/catalog?collection=${activeCollectionTab}`}
              className="hidden lg:flex flex-col justify-center items-center text-center p-6 border border-dashed border-border/80 hover:border-primary/60 transition group bg-neutral-500/5"
            >
              <ArrowRight className="w-8 h-8 text-foreground/40 group-hover:text-primary transition group-hover:translate-x-1 duration-300" />
              <span className="text-xs uppercase tracking-widest font-bold text-foreground mt-4">
                View All {activeCollectionTab}
              </span>
              <span className="text-[10px] text-foreground/50 mt-1 uppercase tracking-wider">
                ({products.filter((p) => p.collections.includes(activeCollectionTab)).length} Items)
              </span>
            </Link>
          </div>
        ) : (
          <div className="text-center py-12 border border-dashed border-border/40 text-foreground/50 uppercase tracking-widest text-xs">
            No items in {activeCollectionTab} collection currently
          </div>
        )}
      </section>

      {/* 3. Latest Arrivals Section */}
      <section className="layout-container space-y-6">
        <div className="flex items-end justify-between border-b border-border/60 pb-4">
          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
              Just Released
            </h2>
            <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wider text-foreground">
              Latest Arrivals
            </h3>
          </div>
          <Link
            href="/catalog?sort=newest"
            className="text-xs uppercase tracking-widest text-foreground hover:text-primary flex items-center gap-1 font-semibold transition"
          >
            See All <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {isLoading ? <ProductLoadingGrid /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {latestArrivals.slice(0, 5).map((product) => (
              <ProductTile key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* 4. Featured Selections Section */}
      <section className="layout-container space-y-6">
        <div className="flex items-end justify-between border-b border-border/60 pb-4">
          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
              Curator Picks
            </h2>
            <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wider text-foreground">
              Featured Selections
            </h3>
          </div>
          <Link
            href="/catalog?tag=featured"
            className="text-xs uppercase tracking-widest text-foreground hover:text-primary flex items-center gap-1 font-semibold transition"
          >
            See All <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {isLoading ? <ProductLoadingGrid /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {featuredSelections.slice(0, 5).map((product) => (
              <ProductTile key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* 5. Travel Sprays & Minis Section */}
      <section className="layout-container space-y-6">
        <div className="flex items-end justify-between border-b border-border/60 pb-4">
          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
              On the Go Luxury
            </h2>
            <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wider text-foreground">
              Travel Sprays & Minis
            </h3>
          </div>
          <Link
            href="/catalog?collection=Travel+Sprays+%26+Minis"
            className="text-xs uppercase tracking-widest text-foreground hover:text-primary flex items-center gap-1 font-semibold transition"
          >
            See All <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {isLoading ? <ProductLoadingGrid /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {travelSprays.slice(0, 5).map((product) => (
              <ProductTile key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* 6. Promotional Banner Section */}
      <section className="layout-container">
        <div className="bg-foreground text-background p-8 sm:p-16 flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
          <div className="space-y-4 max-w-xl text-center md:text-left z-10">
            <span className="inline-flex items-center gap-1.5 bg-primary/20 text-primary px-3 py-1 text-[10px] uppercase tracking-widest font-semibold">
              <Tag className="w-3 h-3" /> Axiom Access Program
            </span>
            <h2 className="text-2xl sm:text-4xl font-light uppercase tracking-wider">
              Exclusive Decants & Samples
            </h2>
            <p className="text-xs sm:text-sm text-background/80 leading-relaxed">
              Experience the material world without scarcity. Sample rare formulations in custom 5ml or 10ml travel vials before committing to full presentations.
            </p>
          </div>
          <div className="z-10 w-full md:w-auto">
            <Link
              href="/catalog?collection=Samples"
              className="block text-center bg-background text-foreground hover:bg-primary hover:text-primary-foreground font-bold px-8 py-4 text-xs uppercase tracking-widest transition-all duration-300 border border-transparent"
            >
              Request Sample Set
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};
export default LandingPageClient;
